import { useRef, type ReactNode } from 'react';
import { useHydrated } from 'remix-utils';
import { cn } from '@rocco/utils/cn';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import { BackToTop } from '~/ui-rocco/components/BackToTop';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { BulkEnquire } from '~/modules/enquiry/bulk/BulkEnquire';
import { BulkEnquiryEnabledProvider } from '~/modules/enquiry/bulk/BulkEnquireTileControl';
import { useSectionInView } from '~/modules/enquiry/bulk/use-section-in-view';
import {
    CONTENT_CAP,
    MagicIsland,
    MagicIslandProvider,
} from '~/ui-rocco/pages/detail-shared';
import {
    DirectoryBrowseThemeProvider,
    type DirectoryBrowseTheme,
} from './directory-browse-theme';
import {
    DirectoryFilterDrawerProvider,
    useDirectoryFilterOpenState,
} from './directory-filter-drawer';

export type DirectoryEntityType = 'products' | 'professionals' | 'projects';

/**
 * Watermark title per vertical. Fixed per directory rather than derived from
 * the active category, so it reads the same at every depth. A directory that
 * lists more than one thing overrides it per mode — see `verticalTitle`.
 */
const VERTICAL_TITLE: Record<DirectoryEntityType, string> = {
    products: 'Products',
    professionals: 'Professionals',
    projects: 'Projects',
};

interface DirectoryBrowsePageProps {
    entityType: DirectoryEntityType;
    /**
     * Overrides the background watermark. Products lists two things under one
     * entity type — products and the brands behind them — so the word has to
     * follow the active browse mode rather than the index being read.
     */
    verticalTitle?: string;
    /**
     * Page theme. Projects renders dark (editorial near-black); products and
     * professionals render light. Default light.
     */
    theme?: DirectoryBrowseTheme;
    /** Eyebrow + editorial heading + Get Matched panel (intro slot). */
    intro?: ReactNode;
    /**
     * Browse-mode nav sitting under the intro (Projects / Spaces / Styles).
     * When supplied the intro's trailing gap tightens so the tabs read as part
     * of the page head rather than a separate band.
     */
    tabs?: ReactNode;
    /** Top "explore categories" image-card carousel (rendered in the category-carousel slot). */
    categoryCarousel?: ReactNode;
    /** Shared filter/category-pill bar for this entity (rendered in the pill-bar slot). */
    pillBar?: ReactNode;
    /**
     * Persistent left filter sidebar (facet panel). When provided, the pill bar's
     * "Show Filters" toggle reveals it beside the grid. Omit for pages without
     * facet filters.
     */
    sidebar?: ReactNode;
    /** Main content — the product/entity grid (rendered in the rows slot). */
    rows?: ReactNode;
    /**
     * True while a full-screen overlay owns the page (the Spaces / Styles image
     * preview). The floating enquiry prompt is suppressed for its duration so
     * the reader is left with the overlay alone.
     */
    overlayOpen?: boolean;
    /**
     * Full-width SSR content rendered below the grid (recently viewed, explore
     * all categories, newspaper FAQ). Sits outside the collapsible sidebar
     * layout so it spans the full page width and stays crawlable.
     */
    belowGrid?: ReactNode;
}

/**
 * Carousel-driven browse experience for the directory pages (5.0 revamp),
 * shared across /products, /professionals and /projects and rendered behind the
 * category_pages_revamp_2026 flag. Composes the shared sections passed in by
 * each entity's browse container with the Figma page spacers between them, and
 * mounts the Magic Island (used by the category carousel for pagination /
 * auto-slide, and later the Bulk Enquire flow).
 */
export const DirectoryBrowsePage = ({
    entityType,
    verticalTitle,
    theme = 'light',
    intro,
    tabs,
    categoryCarousel,
    pillBar,
    sidebar,
    rows,
    overlayOpen = false,
    belowGrid,
}: DirectoryBrowsePageProps) => {
    const hydrated = useHydrated();
    const isDark = theme === 'dark';
    // Filter sidebar starts closed (grid full-width); the pill-bar toggle opens
    // it. The open state is persisted per entity so it survives navigating into
    // a detail page and back — and a full refresh — which keeps the grid columns
    // (and thus the scroll restore) consistent. Only meaningful when a sidebar
    // slot is supplied.
    const { open: filtersOpen, toggle: toggleFilters } =
        useDirectoryFilterOpenState(entityType);
    const hasSidebar = !!sidebar;
    // Grid-scoped "Start an enquiry list" island prompt (bulk mode off): shown
    // while the reading line is inside the grid, so the island stays free up top.
    const rowsRef = useRef<HTMLElement>(null);
    // Scroll behaviour lives in DirectoryUniformGrid, which owns this route's
    // scroll state: a category PUSH lands on the head of the grid, a filter or
    // sort PUSH holds the current position, and POP restores the saved depth.
    const gridInView = useSectionInView(rowsRef);

    return (
        <MagicIslandProvider>
            <BulkEnquiryEnabledProvider value={true}>
                <DirectoryBrowseThemeProvider value={theme}>
                    <DirectoryFilterDrawerProvider
                        value={{
                            open: filtersOpen,
                            toggle: toggleFilters,
                            hasSidebar,
                        }}
                    >
                        {/* Full-bleed dark background so the editorial theme runs edge to
                    edge beyond the centred max-width content. */}
                        <div className={cn(isDark && 'bg-[#141414]')}>
                            {/* Sections cap their content individually (rather
                                than one shared max-width column) so the
                                carousel and pill-bar strips can run full-bleed
                                to the viewport edge on >1920 screens while
                                everything else centres in the 1920 container. */}
                            <div
                                data-testid="directory-browse-page"
                                data-entity-type={entityType}
                                className="relative flex w-full flex-col"
                            >
                                {/* The vertical's name, set very large and
                                    barely off the page colour, centred on the
                                    header's logomark. Named from `entityType`,
                                    not the active category — it is the library
                                    you are in, and it stays put however deep
                                    you go.

                                    It has to clear the site header, which is
                                    opaque and z-120: safe because the header's
                                    right half is empty across that band, and
                                    stays empty when the nav menu opens.
                                    pointer-events-none so it can never
                                    intercept a click; aria-hidden because the
                                    vertical is already announced by the nav and
                                    the breadcrumb. Desktop only — below md the
                                    page's own h1 carries the name.

                                    The offset is per-theme because the two
                                    headers occupy space differently: the light
                                    header is solid and in-flow, so the page
                                    column already starts below it and the mark
                                    reaches back up over it. The dark header is
                                    transparent and absolute, so the column
                                    starts at the viewport top and the mark has
                                    to come down to meet the logomark instead —
                                    a shared offset puts it off-screen. */}
                                <div
                                    aria-hidden="true"
                                    className={cn(
                                        CONTENT_CAP,
                                        'pointer-events-none absolute inset-x-0 z-[121] px-4 max-md:hidden md:px-12',
                                        isDark
                                            ? 'top-[14.55rem]'
                                            : '-top-[9.1rem]',
                                    )}
                                >
                                    <p
                                        className={cn(
                                            'm-0 text-right text-[7.76rem] leading-none font-medium tracking-[-0.05em] whitespace-nowrap',
                                            // Dark needs the wider step off the
                                            // page colour: the same 9-unit gap
                                            // that reads as a watermark against
                                            // white disappears against near-black.
                                            isDark
                                                ? 'text-[#2b2b2b]'
                                                : 'text-[#f6f6f6]',
                                        )}
                                    >
                                        {verticalTitle ??
                                            VERTICAL_TITLE[entityType]}
                                    </p>
                                </div>
                                {/* Leading spacer between the top nav and the page
                            content. On the dark theme the site header runs
                            transparent and absolute (it overlaps rather than
                            reserving layout height), so the spacer must also clear
                            the header — the Figma's 376px Top Nav + 160px gap —
                            instead of the 160px used under the solid header.
                            Mobile has no dark Figma: the dark value is the
                            measured 76px fixed header plus the light theme's
                            48px gap, keeping first-content rhythm identical
                            across themes instead of inheriting a desktop-scaled
                            void. */}
                                {/* The dark value is the light one plus the
                                    header the transparent theme has to clear,
                                    so both themes open on the same gap between
                                    the header and the first line of copy. Only
                                    the light half carries the design's spacing
                                    — the rest is the header's own height and
                                    must not be scaled with it. */}
                                <SectionSpacer
                                    height={isDark ? 506 : 128}
                                    mobileHeight={isDark ? 114 : 38}
                                    unit="rem"
                                />

                                {intro ? (
                                    <>
                                        <section
                                            data-section="intro"
                                            className={cn(
                                                CONTENT_CAP,
                                                'px-4 md:px-12',
                                            )}
                                        >
                                            {intro}
                                        </section>
                                        {/* The pill row's sticky band carries
                                            its own top padding, so this spacer
                                            only makes up the difference to the
                                            intended gap. Every spacer on this
                                            page sits at 80% of its Figma
                                            value — the design's rhythm, tighter. */}
                                        <SectionSpacer
                                            height={tabs ? 90 : 67}
                                            mobileHeight={tabs ? 40 : 45}
                                            unit="rem"
                                        />
                                    </>
                                ) : null}

                                {/* Browse-mode tabs (Figma 5762:114114), on
                                    the same content inset as the intro so the
                                    tab rule lines up with the statement. */}
                                {tabs ? (
                                    <>
                                        <section
                                            data-section="browse-tabs"
                                            className={cn(
                                                CONTENT_CAP,
                                                'px-4 md:px-12',
                                            )}
                                        >
                                            {tabs}
                                        </section>
                                        <SectionSpacer
                                            height={53}
                                            mobileHeight={24}
                                            unit="rem"
                                        />
                                    </>
                                ) : null}

                                {/* Category-card carousel (includes its own heading + separator) */}
                                <section data-section="category-carousel">
                                    {categoryCarousel}
                                </section>

                                {/* mobileHeight 0: the sticky pill bar carries
                                    its own mobile padding, so a spacer here
                                    reads as a random gap under the carousel. */}
                                {categoryCarousel ? (
                                    <SectionSpacer
                                        height={128}
                                        mobileHeight={0}
                                        unit="rem"
                                    />
                                ) : null}

                                {/* Filter / category-pill bar */}
                                {/* Below md the pill bar snaps to the fixed
                                    header's bottom edge (Pinterest-style) and
                                    the grid scrolls beneath it — its own
                                    background, sitting under the header's
                                    z-120. 4.75rem = the header's 76px at the
                                    414 anchor; rem tracks the vw-scaled root
                                    so the offset follows the header at every
                                    width. Desktop keeps the in-flow bar. */}
                                <section
                                    data-section="pill-bar"
                                    // qhd:overflow-x-clip — the bar's pill
                                    // scroller breaks out to the viewport edge
                                    // with a 100vw-based margin; clip here so a
                                    // classic (non-overlay) scrollbar's width
                                    // can't create a horizontal page scroll.
                                    className={cn(
                                        'max-md:sticky max-md:top-[4.75rem] max-md:z-[110] max-md:pt-2 max-md:pb-2.5 qhd:overflow-x-clip',
                                        // Desktop: this section stops being a
                                        // box, so the pill row inside sticks
                                        // against the page column rather than
                                        // against these two rows. A sticky
                                        // element only travels within its
                                        // parent's box — kept as a section, the
                                        // bar would unstick as soon as the
                                        // heading row below it scrolled past.
                                        // The pill row carries its own sticky
                                        // and frosted fill (DirectoryPillBar).
                                        'md:contents',
                                        isDark
                                            ? 'max-md:bg-[#141414]'
                                            : 'max-md:bg-white',
                                    )}
                                >
                                    {pillBar}
                                </section>

                                {/* Main grid, with the optional facet sidebar collapsing
                            in on the left when Show Filters is toggled. */}
                                {/* max-md:pt-6 — the visual gap below the pill
                                    bar lives here (outside the sticky strip) so
                                    it scrolls under the bar once stuck. */}
                                <section
                                    data-section="rows"
                                    ref={rowsRef}
                                    className={cn(CONTENT_CAP, 'max-md:pt-6')}
                                >
                                    {hasSidebar ? (
                                        <CollapsiblePageLayout
                                            sidePanel={sidebar}
                                            hideSidePanel={!filtersOpen}
                                            disableMinHeight
                                            paddingClassName="px-0"
                                        >
                                            {rows}
                                        </CollapsiblePageLayout>
                                    ) : (
                                        rows
                                    )}
                                </section>

                                {/* Below-grid SEO content (recently viewed, explore
                            all categories, newspaper FAQ). Full width, outside
                            the sidebar layout — reachable now that the grid's
                            capped load lets users scroll past it. */}
                                {belowGrid ? (
                                    <section
                                        data-section="below-grid"
                                        className={CONTENT_CAP}
                                    >
                                        {belowGrid}
                                    </section>
                                ) : null}
                            </div>
                        </div>
                    </DirectoryFilterDrawerProvider>
                </DirectoryBrowseThemeProvider>
            </BulkEnquiryEnabledProvider>

            {/* Fixed chrome, so it sits outside the theme provider's subtree
                and takes the theme by prop. */}
            {hydrated ? <BackToTop isDark={isDark} /> : null}
            {hydrated ? <MagicIsland /> : null}
            {hydrated ? (
                <BulkEnquire
                    gridInView={gridInView && !overlayOpen}
                    entityType={entityType}
                />
            ) : null}
        </MagicIslandProvider>
    );
};
